/**
 * src/modules/_tickets-bus.ts — Sprint 25 (2026-05-27)
 *
 * Centralny event-bus dla zakładki "Obsługa klienta" (`/tickets`). Każda akcja
 * w drawerach (after-sales, customer-service messaging, messaging-unified) która
 * zmienia stan po stronie backendu, emituje event przez `emitTicketsEvent(...)`.
 *
 * Konsumenci (`customer-service.ts` lista, `_sidebar-customer-service-badge.ts`)
 * subskrybują przez `onTicketsEvent(handler)` i wykonują surgical update (usuń/zmień
 * kafelek + decrement licznika kindów) zamiast czekać 60s na polling.
 *
 * Mechanizm hybrydowy:
 *   1) window.dispatchEvent — listenery w tej samej karcie
 *   2) BroadcastChannel('sellbase-tickets') — sync między kartami w tej samej
 *      przeglądarce (np. /tickets w karcie A, /disputes w karcie B)
 *
 * Idempotencja: każdy event ma `eventId` (uuid). Odbiorca trzyma `Set` ostatnich
 * 200 ID — duplikaty (echo BC do nadawcy, lub multi-listener) są ignorowane.
 *
 * Fallback: jeśli event się zgubi (tab crash, BC desync), polling co
 * `refresh_interval_sec` (z tickets_config, default 60s) w `customer-service.ts`
 * pokryje stan. SLA: max 60s opóźnienia jeśli event lost.
 *
 * Faza 2 (TBD): backend emituje SSE → ten sam bus odbiera cross-device.
 */

export type TicketsEventType =
  | 'sb:tickets:case-replied'           // Seller odpisał (claim/dispute message)
  | 'sb:tickets:case-status-changed'    // Decyzja claim, refund, status zmienił się
  | 'sb:tickets:case-closed'            // Sprawa zamknięta (claim-accepted/rejected)
  | 'sb:tickets:return-status-changed'  // Status zwrotu Allegro zmieniony
  | 'sb:tickets:thread-replied'         // Seller wysłał msg w wątku messaging
  | 'sb:tickets:thread-auto-replied'    // Auto-reply worker odpisał za sellera
  | 'sb:tickets:invalidate';            // Hard reset — full reload

export interface TicketsEventPayload {
  caseId?: number;
  threadId?: string;
  accountId?: number;
  kind?: 'DISPUTE' | 'CLAIM' | 'RETURN' | 'MESSAGE';
  oldStatus?: string;
  newStatus?: string;
  newStatusCode?: string;
  reason?: string;
  source?: string;        // debug: 'after-sales-drawer', 'cs-msg-drawer', 'messaging-page'
  eventId?: string;       // dedup
  ts?: number;            // epoch ms
}

// Channel name dla BroadcastChannel — single name dla całej zakładki tickets
const _BC_CHANNEL_NAME = 'sellbase-tickets';

let _bc: BroadcastChannel | null = null;
function _getBC(): BroadcastChannel | null {
  if (_bc) return _bc;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    _bc = new BroadcastChannel(_BC_CHANNEL_NAME);
  } catch (_) {
    return null;
  }
  return _bc;
}

function _uuid(): string {
  // Prosty randomny ID — nie potrzebujemy kryptograficznej jakości
  return 'tev_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Emituje event. Wywołaj po udanym backend write (status 200/201/204).
 * Wzbogaca payload o eventId + ts jeśli brakuje.
 */
export function emitTicketsEvent(type: TicketsEventType, payload: TicketsEventPayload = {}): void {
  const fullPayload: TicketsEventPayload = {
    ...payload,
    eventId: payload.eventId || _uuid(),
    ts: payload.ts || Date.now(),
  };
  try {
    window.dispatchEvent(new CustomEvent(type, { detail: fullPayload }));
  } catch (_) {}
  // BroadcastChannel — multi-tab sync. Tab który emit'uje TEŻ dostaje swój własny
  // msg (BroadcastChannel semantyka), więc dedup po eventId chroni przed double-handle.
  const bc = _getBC();
  if (bc) {
    try { bc.postMessage({ type, payload: fullPayload }); } catch (_) {}
  }
}

/**
 * Subskrybuje wszystkie eventy `sb:tickets:*`. Zwraca unsubscribe function.
 *
 * Dedup: każdy odbiorca ma swój `Set<eventId>` — duplikaty (window event + BC echo,
 * lub 2x BC z różnych źródeł) są ignorowane. Set ograniczony do ostatnich 200 ID.
 */
export function onTicketsEvent(
  handler: (type: TicketsEventType, payload: TicketsEventPayload) => void
): () => void {
  const seen = new Set<string>();
  const maxSize = 200;

  function _dedupAndCall(type: TicketsEventType, payload: TicketsEventPayload) {
    const id = payload.eventId;
    if (id) {
      if (seen.has(id)) return; // duplikat
      seen.add(id);
      // FIFO ograniczenie
      if (seen.size > maxSize) {
        const first = seen.values().next().value;
        if (first) seen.delete(first);
      }
    }
    try { handler(type, payload); } catch (e) { console.warn('[tickets-bus] handler error:', e); }
  }

  const types: TicketsEventType[] = [
    'sb:tickets:case-replied',
    'sb:tickets:case-status-changed',
    'sb:tickets:case-closed',
    'sb:tickets:return-status-changed',
    'sb:tickets:thread-replied',
    'sb:tickets:thread-auto-replied',
    'sb:tickets:invalidate',
  ];
  const winListeners: Array<[string, (e: Event) => void]> = [];
  for (const t of types) {
    const fn = (e: Event) => {
      const ce = e as CustomEvent<TicketsEventPayload>;
      _dedupAndCall(t, ce.detail || {});
    };
    window.addEventListener(t, fn);
    winListeners.push([t, fn]);
  }

  const bc = _getBC();
  const bcListener = bc
    ? (e: MessageEvent) => {
        const data = e.data || {};
        const t = data.type as TicketsEventType;
        if (!t || !t.startsWith('sb:tickets:')) return;
        _dedupAndCall(t, data.payload || {});
      }
    : null;
  if (bc && bcListener) bc.addEventListener('message', bcListener);

  // unsubscribe
  return function unsubscribe() {
    for (const [t, fn] of winListeners) window.removeEventListener(t, fn);
    if (bc && bcListener) try { bc.removeEventListener('message', bcListener); } catch (_) {}
  };
}

// Eksport na window dla backward-compat i debugowania z konsoli
if (typeof window !== 'undefined') {
  (window as any).SellBaseTicketsBus = { emitTicketsEvent, onTicketsEvent };
}
